import { useEffect, useState } from "react";
import {
  animate,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { fmt } from "@/lib/format";

export interface NumberTickerProps {
  /** Final numeric value to display. */
  value: number;
  /** Animation duration in ms. Defaults to 700ms. */
  duration?: number;
  /** Optional formatter; defaults to the compact `fmt` helper. */
  format?: (n: number) => string;
  /** Optional className forwarded to the wrapper span. */
  className?: string;
}

/**
 * Animates a number from 0 to `value` using an easeOutExpo curve. Respects
 * `prefers-reduced-motion` — falls back to the final value with no tween.
 * Exposes an `aria-live="polite"` region so screen readers announce updates.
 */
export function NumberTicker({
  value,
  duration = 700,
  format,
  className,
}: NumberTickerProps) {
  const reduced = useReducedMotion();
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => (format ?? fmt)(v));
  const [display, setDisplay] = useState(() =>
    (format ?? fmt)(reduced ? value : 0),
  );

  useEffect(() => {
    if (reduced) {
      setDisplay((format ?? fmt)(value));
      return;
    }
    const controls = animate(mv, value, {
      duration: duration / 1000,
      // easeOutExpo — fast start, settles into the final value.
      ease: [0.16, 1, 0.3, 1],
    });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, duration, reduced, mv, rounded, format]);

  return (
    <span className={className} aria-live="polite">
      {display}
    </span>
  );
}
